<template>
  <div :style="{ width: '1000px' }">
    <v-card class="d-flex pa-1 bg" outlined>
      <div class="mr-auto d-flex">
        <system-linked-combo v-model="select" :last-item="{ value: '*', text: 'Default' }"></system-linked-combo>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" class="ml-2" x-small color="primary" @click="fetchData(1)" @click.ctrl.alt="fetchData(0)">
              <v-icon small>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.retrieve') }}</span>
        </v-tooltip>
      </div>
    </v-card>
    <!-- eslint-disable-next-line prettier/prettier -->
    <v-data-table
      :headers="headers" :items="items" item-key="sequence" :loading="loading"
      class="elevation-1 content" dense fixed-header disable-pagination hide-default-footer height="560px"
    >
      <template v-slot:top>
        <KpiForm :isShow="showForm" :formData="formItem" @formClose="formClose" />
      </template>
      <template v-slot:item.warningValue="{ item }">
        <span v-if="item.alertType != 'EVENT'">{{ item.warningValue }}</span>
      </template>
      <template v-slot:item.criticalValue="{ item }">
        <span v-if="item.alertType != 'EVENT'">{{ item.criticalValue }}</span>
      </template>
      <template v-slot:item.collectCount="{ item }">
        <span v-if="item.alertType != 'EVENT'">{{ item.collectCount }}</span>
      </template>
      <template v-slot:item.unitSymbol="{ item }">
        <span v-if="item.alertType != 'EVENT'">{{ item.unitSymbol }}</span>
      </template>
      <template v-slot:item.active="{ item }">
        <v-simple-checkbox v-model="item.active" disabled class="sp-chk-dense chk-align-c"></v-simple-checkbox>
      </template>
      <template v-slot:item.continuousAlert="{ item }">
        <v-simple-checkbox v-if="item.alertType != 'EVENT'" v-model="item.continuousAlert" disabled class="sp-chk-dense chk-align-c"></v-simple-checkbox>
      </template>
      <template v-slot:item.failback="{ item }">
        <v-simple-checkbox v-if="item.alertType != 'EVENT'" v-model="item.failback" disabled class="sp-chk-dense chk-align-c"></v-simple-checkbox>
      </template>
      <template v-if="fetchAll" v-slot:item.display="{ item }">
        <v-simple-checkbox v-model="item.display" class="check-margin" @click="checkDisplay(item)"></v-simple-checkbox>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small color="primary" @click="editItem(item)">mdi-pencil-box-multiple-outline</v-icon>
          </template>
          <span>{{ $t('common.edit') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { api } from '@/api/common/instances';
import KpiForm from './KpiForm';

export default {
  name: 'Kpi',
  components: {
    KpiForm,
  },
  data() {
    return {
      loading: true,
      select: { stack: 'ABAP', system: '' },
      items: [],
      showForm: false,
      formItem: {},
      headerBase: [
        { text: this.$t('alert.description'), value: 'message', width: '34%', sortable: false },
        { text: this.$t('alert.warning'), value: 'warningValue', sortable: false, align: 'center' },
        { text: this.$t('alert.critical'), value: 'criticalValue', sortable: false, align: 'center' },
        { text: this.$t('alert.count'), value: 'collectCount', sortable: false, align: 'center' },
        { text: this.$t('alert.decimalFormat'), value: 'unitSymbol', sortable: false, align: 'center' },
        { text: this.$t('alert.active'), value: 'active', sortable: false, align: 'center' },
        { text: this.$t('alert.continuousAlert'), value: 'continuousAlert', sortable: false, align: 'center' },
        { text: this.$t('alert.failback'), value: 'failback', sortable: false, align: 'center' },
      ],
      headerDisplay: [{ text: this.$t('alert.display'), value: 'display', sortable: false, align: 'center' }],
      headerAction: [{ text: '', value: 'actions', align: 'center', sortable: false }],
      fetchAll: false,
    };
  },
  computed: {
    headers() {
      if (this.fetchAll) return [...this.headerBase, ...this.headerDisplay, ...this.headerAction];
      else return [...this.headerBase, ...this.headerAction];
    },
  },
  watch: {
    select: {
      deep: true,
      handler() {
        this.fetchData(1);
      },
    },
  },
  methods: {
    /**
     * . 조회 버튼을 그냥 클릭 시에는
     *   dsplOptn에 'D'가 전달되어 서버에서 CONF_ALRT_CODE.IS_DSPL='1'인 내역만 읽어준다.
     * . 조회 버튼 클릭 시 Ctrl+Alt를 누르고 클릭하면 모든 내역을 다 읽어준다.
     *   이는 OWLens 기술지원 인력이 필요에 따라
     *   코드 내역을 이 화면에 표시해줄 지 여부를 편집할 수 있게 하기 위한 기능이다.
     *   또한, dspl 조건으로 0이 설정된 경우에는 목록에서 "표시영부(IS_DSPL)" 컬럼을 보여주고
     *   check-box가 클릭될 때 바로 해당 내역의 IS_DSPL 컬럼 값을 DB에서 변경하여 준다.
     * . 사실 이 화면은 조회 버튼이 필요없는데 위와 같은 기능 구현을 위해 존재한다.
     */
    async fetchData(dspl) {
      this.loading = true;
      this.fetchAll = dspl == 0 ? true : false;
      const params = {
        stackType: this.select.stack,
        systemId: this.select.system,
        display: dspl,
      };
      const { data } = await api.get('/preference/alert-codes', { params });
      // Description 값으로 정렬을 해주어서 어떤 locale에서든 목록 탐색 용이성을 부여한다.
      data.sort((a, b) => a.message.localeCompare(b.message));
      this.items = data;
      this.loading = false;
    },
    async checkDisplay(item) {
      const param = new URLSearchParams();
      param.append('systemId', item.systemId);
      param.append('sapDataCode', item.sapDataCode);
      param.append('alertCode', item.alertCode);
      param.append('display', item.display);
      const rspn = await api.post('/preference/update-alert-code-is-display', param);
      if (rspn.status == 200) {
        this.$dialog.info(this.$t('common.successful'));
      }
    },
    editItem(item) {
      this.formItem = item;
      this.showForm = true;
    },
    formClose(param) {
      this.showForm = false;
      if (param == 'save') this.fetchData(1);
    },
  },
};
</script>
